import { ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  UserRound,
  LogOut,
  CircleHelp,
  Wrench,
  Briefcase,
  BarChart3,
  ClipboardList,
  CreditCard,
  Package,
  Activity,
  MapPin,
  Factory,
  Waves,
  FileText,
  ShoppingCart,
  UsersRound,
  Calculator,
} from 'lucide-react';
import { signOut } from '../modules/user/actions';
import { openProfileDrawer } from '../modules/drawer/actions';
import AdminOnly, { AdminHenningHans, Aks2talOnly, isAks2talOnly } from '../components/AdminOnly';
import { useAppSelector } from '../hooks';
import { useIsMobile } from '../hooks/useIsMobile';
import { pipedriveAppEnabled } from 'lib/pipedriveApp';
import { Button } from 'components/ui/button';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from 'components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from 'components/ui/dropdown-menu';

export default function MenuHeader() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const uid = useAppSelector((state) => state.user.details.uid);
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const current = (path: string) =>
    path === '/orders' ? pathname === path : pathname === path || pathname.startsWith(`${path}/`);
  const item = (path: string, label: string, Icon: typeof UserRound) => {
    const link = (
      <Link
        to={path}
        onClick={() => setOpen(false)}
        aria-current={current(path) ? 'page' : undefined}
        className={`flex items-center gap-3 rounded-md px-2 py-2.5 text-sm !text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          current(path) ? 'bg-secondary font-medium' : ''
        }`}
      >
        <Icon aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
        {t(label)}
      </Link>
    );
    return isMobile ? (
      <div key={path}>{link}</div>
    ) : (
      <DropdownMenuItem key={path} asChild className="cursor-pointer">
        {link}
      </DropdownMenuItem>
    );
  };
  const section = (title: string, Icon: typeof UserRound, children: ReactNode) =>
    isMobile ? (
      <details key={title} className="group border-b py-1">
        <summary className="flex cursor-pointer list-none items-center gap-3 rounded-md py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
          <Icon className="h-4 w-4" aria-hidden="true" />
          {t(title)}
          <ChevronDown
            className="ml-auto h-4 w-4 transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className="mb-2 ml-2 border-l pl-3">{children}</div>
      </details>
    ) : (
      <DropdownMenuSub key={title}>
        <DropdownMenuSubTrigger className="gap-3 py-2.5">
          <Icon aria-hidden="true" />
          {t(title)}
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent
            collisionPadding={12}
            className="w-60 max-w-[calc(100vw-24px)] [&_svg]:h-4 [&_svg]:w-4"
          >
            {children}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    );
  const openProfile = () => {
    setOpen(false);
    dispatch(openProfileDrawer());
  };
  const logout = () => {
    dispatch(signOut());
    window.location.reload();
  };
  const trigger = (
    <Button variant="outline" size="sm" className="h-9 gap-2" aria-label={t('header.menu')}>
      <UserRound aria-hidden="true" />
      <span>{t('header.menu')}</span>
      <ChevronDown aria-hidden="true" />
    </Button>
  );
  const content = (
    <>
      {isAks2talOnly(uid) && (
        <>
          {!isMobile && (
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t('Arbejdsområder')}
            </DropdownMenuLabel>
          )}
          {section(
            'Drift og udstyr',
            Wrench,
            <>
              <AdminOnly>
                {item('/tasks', 'header.tasks', ClipboardList)}
                {item('/inventory', 'header.inventory', Package)}
                {item('/sensors', 'Alarmoverblik', Activity)}
                {item('/gps-overview', 'GPS overblik', MapPin)}
              </AdminOnly>
              <AdminHenningHans>
                {item('/plants-overview', 'Anlæg overblik', Factory)}
              </AdminHenningHans>
              <Aks2talOnly>{item('/level-sensors', 'Niveau målinger', Waves)}</Aks2talOnly>
            </>
          )}
          {section(
            'Salg og kunder',
            Briefcase,
            <>
              <Aks2talOnly>
                {item('/agreements', 'header.agreements', FileText)}
                {item('/orders', 'Ordre', ShoppingCart)}
                {item('/orders/prognosis', 'Kundeopfølgning', UsersRound)}
                {process.env.REACT_APP_CRM_ENABLED === 'true' && item('/crm', 'CRM', UsersRound)}
              </Aks2talOnly>
              <AdminOnly>
                {item('/billing', 'header.billing', CreditCard)}
                {item('/sales-overview', 'Salgsoversigt', BarChart3)}
                {pipedriveAppEnabled() && item('/pipedrive', 'header.pipedrive', Briefcase)}
              </AdminOnly>
            </>
          )}
          {section(
            'Rapporter og værktøjer',
            FileText,
            <Aks2talOnly>
              {item('/dust-reports', 'Støvrapporter', FileText)}
              {item('/dust-binder-calculator', 'Støvbinder beregner', Calculator)}
            </Aks2talOnly>
          )}
          {!isMobile && <DropdownMenuSeparator />}
        </>
      )}
      {isMobile ? (
        <p className="mb-1 mt-5 text-xs font-medium text-muted-foreground">{t('Din konto')}</p>
      ) : (
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('Din konto')}
        </DropdownMenuLabel>
      )}
      {isMobile ? (
        <Button variant="ghost" className="w-full justify-start gap-3 px-2" onClick={openProfile}>
          <UserRound aria-hidden="true" />
          {t('header.profile')}
        </Button>
      ) : (
        <DropdownMenuItem className="cursor-pointer gap-3 py-2.5" onSelect={openProfile}>
          <UserRound aria-hidden="true" />
          {t('header.profile')}
        </DropdownMenuItem>
      )}
      {item('/faq', 'FAQ', CircleHelp)}
      {isMobile ? <div className="my-2 border-t" /> : <DropdownMenuSeparator />}
      {isMobile ? (
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 px-2 text-destructive"
          onClick={logout}
        >
          <LogOut aria-hidden="true" />
          {t('header.signout')}
        </Button>
      ) : (
        <DropdownMenuItem
          className="cursor-pointer gap-3 py-2.5 text-destructive focus:text-destructive"
          onSelect={logout}
        >
          <LogOut aria-hidden="true" />
          {t('header.signout')}
        </DropdownMenuItem>
      )}
    </>
  );
  if (isMobile)
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent className="w-[min(360px,100vw)] overflow-y-auto px-5">
          <SheetTitle>{t('header.menu')}</SheetTitle>
          <SheetDescription className="sr-only">{t('Arbejdsområder og konto')}</SheetDescription>
          <nav aria-label={t('Menu og konto')} className="mt-5">
            {content}
          </nav>
        </SheetContent>
      </Sheet>
    );
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        collisionPadding={12}
        className="w-60 max-w-[calc(100vw-24px)] [&_svg]:h-4 [&_svg]:w-4"
      >
        {content}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
